import type {
  SecretlintRepositoryListResponse,
  SecretlintRescanResponse,
  SecretlintRunDetailResponse,
  SecretlintRunListResponse,
} from './types.js';

/**
 * @graph-stack repository-secretlint
 * @graph-domain Engineering
 * @graph-business API client for the repository-secretlint remote. Calls Repository Console security endpoints from the remote UI.
 * @graph-connects /security/secretlint/* [calls] Secretlint security API endpoints
 */

interface ErrorResponse {
  error?: {
    message?: string;
  };
}

/**
 * Secretlint API client error.
 * @graph-connects none
 */
export class SecretlintApiError extends Error {
  /** @graph-connects none */
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = 'SecretlintApiError';
  }
}

/**
 * Create a scoped Secretlint API client.
 * @graph-connects /security/secretlint/* [calls] Secretlint security API endpoints
 */
export function createSecretlintApi(apiBaseUrl = '/api') {
  const request = async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
    const headers = new Headers(options.headers);
    headers.set('Content-Type', 'application/json');

    const response = await fetch(`${apiBaseUrl}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const error = (await response.json().catch(() => ({}))) as ErrorResponse;
      throw new SecretlintApiError(
        error.error?.message ?? `HTTP ${String(response.status)}`,
        response.status,
      );
    }

    return response.json() as Promise<T>;
  };

  return {
    listRepositories: () =>
      request<SecretlintRepositoryListResponse>('/security/secretlint/repositories'),
    listRuns: (repositoryId: string) =>
      request<SecretlintRunListResponse>(
        `/security/secretlint/repositories/${encodeURIComponent(repositoryId)}/runs`,
      ),
    getRun: (runId: string) =>
      request<SecretlintRunDetailResponse>(
        `/security/secretlint/runs/${encodeURIComponent(runId)}`,
      ),
    rescan: (repositoryId: string) =>
      request<SecretlintRescanResponse>(
        `/security/secretlint/repositories/${encodeURIComponent(repositoryId)}/rescan`,
        {
          method: 'POST',
          body: JSON.stringify({ scope: 'default_branch' }),
        },
      ),
  };
}
